import { useState, useEffect, useLayoutEffect, useRef, useCallback, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import './SupportChat.css';
import AuthContext from '../../Context/AuthContext';
import LanguageContext from '../../Context/LanguageContext';
import { supportChatAPI, createSupportSocket } from '../../Api/supportChatApi';
import { useTranslation } from 'react-i18next';
import { MessageCircle, X, Send, Headset, LogIn, Loader2, ChevronDown, MessagesSquare, Paperclip } from 'lucide-react';

function formatTime(value) {
    if (!value) return '';
    return new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function isImageAttachment(attachment) {
    return attachment?.mimeType?.startsWith('image/');
}

function MessageAttachment({ attachment, t }) {
    if (!attachment?.url) return null;

    if (isImageAttachment(attachment)) {
        return (
            <a
                className="support-chat__attachment-image-link"
                href={attachment.url}
                target="_blank"
                rel="noopener noreferrer"
            >
                <img
                    className="support-chat__attachment-image"
                    src={attachment.url}
                    alt={attachment.filename || t('supportChat.attachment', { defaultValue: 'Attachment' })}
                />
            </a>
        );
    }

    return (
        <a
            className="support-chat__attachment-file"
            href={attachment.url}
            target="_blank"
            rel="noopener noreferrer"
        >
            📎 {attachment.filename || t('supportChat.downloadAttachment', { defaultValue: 'Download attachment' })}
        </a>
    );
}

const MESSAGE_PAGE_SIZE = 50;
const SCROLL_BOTTOM_THRESHOLD = 64;
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const ALLOWED_ATTACHMENT_TYPES = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const SupportChat = () => {
    const { user } = useContext(AuthContext);
    const { currentLanguage } = useContext(LanguageContext);
    const { t } = useTranslation();
    const location = useLocation();
    const [token, setToken] = useState(() => localStorage.getItem('token'));
    const isLoggedIn = Boolean(user?._id && token);
    const lang = currentLanguage || 'en';

    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [conversationId, setConversationId] = useState(null);
    const [threadStatus, setThreadStatus] = useState('open');
    const [storeOpen, setStoreOpen] = useState(true);
    const [unread, setUnread] = useState(0);
    const [text, setText] = useState('');
    const [pendingFile, setPendingFile] = useState(null);
    const [sendError, setSendError] = useState('');
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingOlder, setLoadingOlder] = useState(false);
    const [messagesPage, setMessagesPage] = useState(1);
    const [hasMoreMessages, setHasMoreMessages] = useState(false);
    const [adminTyping, setAdminTyping] = useState(false);
    const [showJumpToBottom, setShowJumpToBottom] = useState(false);

    const socketRef = useRef(null);
    const openRef = useRef(open);
    const endRef = useRef(null);
    const inputRef = useRef(null);
    const fileInputRef = useRef(null);
    const panelRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const stickToBottomRef = useRef(true);
    const forceInstantScrollRef = useRef(false);
    const skipSmoothScrollRef = useRef(false);
    const typingTimeoutRef = useRef(null);
    const typingSentRef = useRef(false);
    const adminTypingTimeoutRef = useRef(null);
    const sessionRef = useRef(0);

    openRef.current = open;

    const closePanel = useCallback(() => {
        setOpen(false);
        setShowJumpToBottom(false);
    }, []);

    const resetChatState = useCallback(() => {
        setMessages([]);
        setConversationId(null);
        setThreadStatus('open');
        setStoreOpen(true);
        setUnread(0);
        setText('');
        setPendingFile(null);
        setSendError('');
        setSending(false);
        setLoading(false);
        setLoadingOlder(false);
        setMessagesPage(1);
        setHasMoreMessages(false);
        setAdminTyping(false);
        setShowJumpToBottom(false);
        typingSentRef.current = false;
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        if (adminTypingTimeoutRef.current) clearTimeout(adminTypingTimeoutRef.current);
    }, []);

    const updateScrollPosition = useCallback(() => {
        const container = messagesContainerRef.current;
        if (!container) return;
        const atBottom =
            container.scrollHeight - container.scrollTop - container.clientHeight <= SCROLL_BOTTOM_THRESHOLD;
        stickToBottomRef.current = atBottom;
        setShowJumpToBottom(!atBottom && messages.length > 0);
    }, [messages.length]);

    const jumpToBottom = useCallback(() => {
        stickToBottomRef.current = true;
        forceInstantScrollRef.current = true;
        const container = messagesContainerRef.current;
        if (container) container.scrollTop = container.scrollHeight;
        setShowJumpToBottom(false);
    }, []);

    useEffect(() => {
        const sync = () => setToken(localStorage.getItem('token'));
        window.addEventListener('storage', sync);
        window.addEventListener('app:auth-cleared', sync);
        window.addEventListener('focus', sync);
        return () => {
            window.removeEventListener('storage', sync);
            window.removeEventListener('app:auth-cleared', sync);
            window.removeEventListener('focus', sync);
        };
    }, []);

    useEffect(() => {
        setToken(localStorage.getItem('token'));
    }, [user?._id, location.pathname]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('openSupport') !== '1') return;
        setOpen(true);
        const url = new URL(window.location.href);
        url.searchParams.delete('openSupport');
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }, []);

    useEffect(() => {
        if (!open) return undefined;
        const onKeyDown = (event) => {
            if (event.key === 'Escape') closePanel();
        };
        window.addEventListener('keydown', onKeyDown);
        const isMobileSheet = window.matchMedia('(max-width: 640px)').matches;
        const previousOverflow = document.body.style.overflow;
        if (isMobileSheet) document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            if (isMobileSheet) document.body.style.overflow = previousOverflow;
        };
    }, [open, closePanel]);

    useEffect(() => {
        if (!open || !isLoggedIn || loading) return undefined;
        const timer = window.setTimeout(() => {
            inputRef.current?.focus({ preventScroll: true });
        }, 120);
        return () => window.clearTimeout(timer);
    }, [open, isLoggedIn, loading]);

    const loadThread = useCallback(async (sessionId, page = 1) => {
        if (page === 1) setLoading(true);
        try {
            const res = await supportChatAPI.getThread({ page, limit: MESSAGE_PAGE_SIZE });
            if (sessionId !== sessionRef.current) return;
            if (page === 1) {
                setMessages(res.data.messages || []);
                setConversationId(res.data.conversation?._id || null);
                setThreadStatus(res.data.conversation?.status || 'open');
                setStoreOpen(res.data.storeStatus?.isOpen !== false);
                setUnread(res.data.conversation?.unreadCount || 0);
                setMessagesPage(1);
                stickToBottomRef.current = true;
                forceInstantScrollRef.current = true;
                setShowJumpToBottom(false);
            }
            setHasMoreMessages(!!res.data.hasMore);
            return res.data;
        } catch (error) {
            if (sessionId !== sessionRef.current) return;
            console.error('Failed to load support chat:', error);
            return null;
        } finally {
            if (sessionId === sessionRef.current && page === 1) setLoading(false);
        }
    }, []);

    const loadOlderMessages = async () => {
        if (!isLoggedIn || loadingOlder || !hasMoreMessages) return;
        setLoadingOlder(true);
        const container = messagesContainerRef.current;
        const previousScrollHeight = container?.scrollHeight || 0;
        const sessionId = sessionRef.current;
        try {
            const nextPage = messagesPage + 1;
            const res = await supportChatAPI.getThread({ page: nextPage, limit: MESSAGE_PAGE_SIZE });
            if (sessionId !== sessionRef.current) return;
            const older = res.data.messages || [];
            stickToBottomRef.current = false;
            setMessages((prev) => {
                const existingIds = new Set(prev.map((m) => String(m._id)));
                const uniqueOlder = older.filter((m) => !existingIds.has(String(m._id)));
                return [...uniqueOlder, ...prev];
            });
            setMessagesPage(nextPage);
            setHasMoreMessages(!!res.data.hasMore);
            requestAnimationFrame(() => {
                if (container) {
                    container.scrollTop = container.scrollHeight - previousScrollHeight;
                    updateScrollPosition();
                }
            });
        } catch (error) {
            console.error('Failed to load older support messages:', error);
        } finally {
            if (sessionId === sessionRef.current) setLoadingOlder(false);
        }
    };

    useEffect(() => {
        if (!isLoggedIn) {
            sessionRef.current += 1;
            socketRef.current?.disconnect();
            socketRef.current = null;
            resetChatState();
            return undefined;
        }

        const sessionId = sessionRef.current;
        loadThread(sessionId);

        const socket = createSupportSocket(token);
        if (!socket) {
            return undefined;
        }
        socketRef.current = socket;

        socket.on('connect', () => {
            loadThread(sessionRef.current);
        });

        socket.on('connect_error', () => {
            if (!localStorage.getItem('token')) {
                window.dispatchEvent(new Event('app:auth-cleared'));
            }
        });

        socket.on('support:message', (payload) => {
            const message = payload?.message;
            if (!message || (message.senderType !== 'admin' && message.senderType !== 'system')) return;
            const wasAtBottom = stickToBottomRef.current;
            setMessages((prev) => {
                if (prev.some((m) => String(m._id) === String(message._id))) return prev;
                return [...prev, message];
            });
            setAdminTyping(false);
            if (openRef.current) {
                supportChatAPI.markRead().catch(() => {});
                if (wasAtBottom) {
                    stickToBottomRef.current = true;
                } else {
                    setShowJumpToBottom(true);
                }
            } else {
                setUnread((count) => count + 1);
            }
        });

        socket.on('support:typing', (payload) => {
            if (payload?.from !== 'admin') return;
            setAdminTyping(!!payload.isTyping);
            if (adminTypingTimeoutRef.current) clearTimeout(adminTypingTimeoutRef.current);
            if (payload.isTyping) {
                adminTypingTimeoutRef.current = setTimeout(() => setAdminTyping(false), 4000);
            }
        });

        socket.on('support:conversation-updated', (payload) => {
            if (payload?.status) setThreadStatus(payload.status);
        });

        return () => {
            socket.off('connect');
            socket.off('connect_error');
            socket.off('support:message');
            socket.off('support:typing');
            socket.off('support:conversation-updated');
            socket.disconnect();
            socketRef.current = null;
        };
    }, [isLoggedIn, token, loadThread, resetChatState]);

    useEffect(() => {
        if (!open || !isLoggedIn) return undefined;

        const refresh = () => {
            if (document.visibilityState === 'visible') {
                loadThread(sessionRef.current);
            }
        };

        window.addEventListener('focus', refresh);
        document.addEventListener('visibilitychange', refresh);
        return () => {
            window.removeEventListener('focus', refresh);
            document.removeEventListener('visibilitychange', refresh);
        };
    }, [open, isLoggedIn, loadThread]);

    useEffect(() => {
        if (!open || !isLoggedIn) return undefined;

        const interval = window.setInterval(() => {
            if (!socketRef.current?.connected) {
                loadThread(sessionRef.current);
            }
        }, 10000);

        return () => window.clearInterval(interval);
    }, [open, isLoggedIn, loadThread]);

    const scrollToBottom = useCallback((instant = false) => {
        const container = messagesContainerRef.current;
        if (!container) return;
        if (instant) {
            container.scrollTop = container.scrollHeight;
            setShowJumpToBottom(false);
            return;
        }
        endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, []);

    useLayoutEffect(() => {
        if (!open || loading || !stickToBottomRef.current) return;
        if (!forceInstantScrollRef.current) return;
        scrollToBottom(true);
        forceInstantScrollRef.current = false;
        skipSmoothScrollRef.current = true;
    }, [open, loading, messages, scrollToBottom]);

    useEffect(() => {
        if (!open || loading || !stickToBottomRef.current) return;
        if (skipSmoothScrollRef.current) {
            skipSmoothScrollRef.current = false;
            return;
        }
        scrollToBottom(false);
    }, [messages, adminTyping, open, loading, scrollToBottom]);

    const handleOpen = () => {
        forceInstantScrollRef.current = true;
        stickToBottomRef.current = true;
        setShowJumpToBottom(false);
        setOpen(true);
        if (!isLoggedIn) return;
        loadThread(sessionRef.current);
        supportChatAPI.markRead().catch(() => {});
        setUnread(0);
    };

    const emitTyping = (isTyping) => {
        if (!conversationId) return;
        socketRef.current?.emit('support:typing', { conversationId, isTyping });
    };

    const handleInputChange = (event) => {
        setText(event.target.value);
        if (sendError) setSendError('');
        if (!typingSentRef.current) {
            typingSentRef.current = true;
            emitTyping(true);
        }
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => {
            typingSentRef.current = false;
            emitTyping(false);
        }, 1500);
    };

    const handleSend = async (event) => {
        event?.preventDefault();
        const value = text.trim();
        if ((!value && !pendingFile) || sending) return;

        setSending(true);
        setSendError('');
        const fileToSend = pendingFile;
        setText('');
        setPendingFile(null);
        typingSentRef.current = false;
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        emitTyping(false);

        const sessionId = sessionRef.current;
        try {
            const res = await supportChatAPI.sendMessage(value, fileToSend);
            if (sessionId !== sessionRef.current) return;
            const message = res.data.message;
            const autoReply = res.data.autoReply;
            if (res.data.storeStatus) setStoreOpen(res.data.storeStatus.isOpen !== false);
            stickToBottomRef.current = true;
            setMessages((prev) => {
                let next = prev;
                if (!next.some((m) => String(m._id) === String(message._id))) next = [...next, message];
                if (autoReply && !next.some((m) => String(m._id) === String(autoReply._id))) {
                    next = [...next, { ...autoReply, senderType: 'system' }];
                }
                return next;
            });
            if (!conversationId) setConversationId(message.conversation);
            setThreadStatus('open');
            setShowJumpToBottom(false);
        } catch (error) {
            if (sessionId !== sessionRef.current) return;
            console.error('Failed to send support message:', error);
            const message = error.response?.data?.message
                || t('supportChat.sendFailed', { defaultValue: 'Could not send message. Please try again.' });
            setSendError(message);
            setText(value);
            setPendingFile(fileToSend);
        } finally {
            if (sessionId === sessionRef.current) setSending(false);
        }
    };

    const handleFileSelect = (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        if (file.size > MAX_ATTACHMENT_BYTES) {
            setSendError(t('supportChat.fileTooLarge', { defaultValue: 'File is too large (max 10 MB).' }));
            return;
        }
        if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
            setSendError(t('supportChat.invalidFile', { defaultValue: 'Unsupported file type.' }));
            return;
        }
        setSendError('');
        setPendingFile(file);
    };

    const headerSubtitle = !isLoggedIn
        ? t('supportChat.signInToChat', { defaultValue: 'Sign in to start chatting' })
        : adminTyping
            ? t('supportChat.typing', { defaultValue: 'Support is typing…' })
            : threadStatus === 'closed'
                ? t('supportChat.closedHint', { defaultValue: 'This conversation was resolved. Send a message to reopen it.' })
                : !storeOpen
                    ? t('supportChat.offlineSubtitle', { defaultValue: "We're away — you'll get an auto-reply and we'll respond when we're back." })
                    : t('supportChat.subtitle', { defaultValue: 'We usually reply shortly' });

    return (
        <div className={`support-chat ${open ? 'support-chat--open' : ''}`}>
            {open && (
                <button
                    type="button"
                    className="support-chat__backdrop"
                    onClick={closePanel}
                    aria-label={t('supportChat.close', { defaultValue: 'Close chat' })}
                />
            )}

            {open && (
                <div
                    ref={panelRef}
                    className="support-chat__panel"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="support-chat-title"
                    aria-describedby="support-chat-subtitle"
                >
                    <header className="support-chat__header">
                        <div className="support-chat__header-info">
                            <span className="support-chat__header-icon" aria-hidden="true">
                                <Headset size={20} />
                            </span>
                            <div className="support-chat__header-text">
                                <p id="support-chat-title" className="support-chat__title">
                                    {t('supportChat.title', { defaultValue: 'Support' })}
                                    {isLoggedIn && (
                                        <span
                                            className={`support-chat__status ${storeOpen ? '' : 'support-chat__status--offline'}`}
                                            title={storeOpen
                                                ? t('supportChat.online', { defaultValue: 'Online' })
                                                : t('supportChat.offline', { defaultValue: 'Away' })}
                                        />
                                    )}
                                </p>
                                <p id="support-chat-subtitle" className="support-chat__subtitle" aria-live="polite">
                                    {headerSubtitle}
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="support-chat__close"
                            onClick={closePanel}
                            aria-label={t('supportChat.close', { defaultValue: 'Close chat' })}
                        >
                            <X size={20} />
                        </button>
                    </header>

                    {!isLoggedIn ? (
                        <div className="support-chat__login-gate">
                            <span className="support-chat__login-icon" aria-hidden="true">
                                <LogIn size={32} />
                            </span>
                            <h4 className="support-chat__login-title">
                                {t('supportChat.loginRequired', { defaultValue: 'Sign in to chat' })}
                            </h4>
                            <p className="support-chat__login-text">
                                {t('supportChat.loginMessage', {
                                    defaultValue: 'Please sign in to message our support team.',
                                })}
                            </p>
                            <div className="support-chat__login-actions">
                                <a className="support-chat__login-btn" href={`/${lang}/sign-in`} onClick={closePanel}>
                                    {t('supportChat.signIn', { defaultValue: 'Sign In' })}
                                </a>
                                <a className="support-chat__login-btn support-chat__login-btn--outline" href={`/${lang}/sign-up`} onClick={closePanel}>
                                    {t('supportChat.signUp', { defaultValue: 'Create account' })}
                                </a>
                            </div>
                        </div>
                    ) : (
                        <div className="support-chat__body">
                            {isLoggedIn && !storeOpen && threadStatus !== 'closed' && (
                                <div className="support-chat__offline-banner" role="status">
                                    {t('supportChat.offlineBanner', {
                                        defaultValue: "Our team is currently offline. You can still leave a message — we'll reply during business hours.",
                                    })}
                                </div>
                            )}
                            {threadStatus === 'closed' && (
                                <div className="support-chat__closed-banner" role="status">
                                    {t('supportChat.closedBanner', {
                                        defaultValue: 'This conversation was marked resolved. You can still send a message to reopen it.',
                                    })}
                                </div>
                            )}
                            <div
                                className="support-chat__messages"
                                ref={messagesContainerRef}
                                onScroll={updateScrollPosition}
                                aria-live="polite"
                                aria-relevant="additions"
                            >
                                {loading ? (
                                    <div className="support-chat__loading" role="status">
                                        <Loader2 className="support-chat__spin" size={24} aria-hidden="true" />
                                        <span>{t('supportChat.loading', { defaultValue: 'Loading messages…' })}</span>
                                    </div>
                                ) : (
                                    <>
                                        {hasMoreMessages && (
                                            <button
                                                type="button"
                                                className="support-chat__load-older"
                                                onClick={loadOlderMessages}
                                                disabled={loadingOlder}
                                            >
                                                {loadingOlder ? (
                                                    <>
                                                        <Loader2 className="support-chat__spin" size={14} aria-hidden="true" />
                                                        <span>{t('supportChat.loadingOlder', { defaultValue: 'Loading…' })}</span>
                                                    </>
                                                ) : (
                                                    <span>{t('supportChat.loadOlder', { defaultValue: 'Load older messages' })}</span>
                                                )}
                                            </button>
                                        )}
                                        {messages.length === 0 ? (
                                            <div className="support-chat__empty">
                                                <MessagesSquare size={28} aria-hidden="true" />
                                                <p>
                                                    {t('supportChat.emptyState', {
                                                        defaultValue: 'Send us a message and our team will get back to you.',
                                                    })}
                                                </p>
                                            </div>
                                        ) : (
                                            messages.map((message) => {
                                                const mine = message.senderType === 'user';
                                                const isSystem = message.senderType === 'system';
                                                return (
                                                    <div
                                                        key={message._id}
                                                        className={`support-chat__msg ${mine ? 'is-mine' : 'is-theirs'} ${isSystem ? 'is-system' : ''}`}
                                                    >
                                                        <div className="support-chat__bubble">
                                                            {isSystem && (
                                                                <span className="support-chat__system-label">
                                                                    {t('supportChat.autoReply', { defaultValue: 'Automated reply' })}
                                                                </span>
                                                            )}
                                                            {message.attachment && (
                                                                <MessageAttachment attachment={message.attachment} t={t} />
                                                            )}
                                                            {message.text ? (
                                                                <span className="support-chat__bubble-text">{message.text}</span>
                                                            ) : null}
                                                            <span className="support-chat__bubble-time">
                                                                {formatTime(message.createdAt)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                        {adminTyping && (
                                            <div className="support-chat__msg is-theirs" aria-label={t('supportChat.typing', { defaultValue: 'Support is typing' })}>
                                                <div className="support-chat__bubble support-chat__bubble--typing">
                                                    <span className="support-chat__dot" />
                                                    <span className="support-chat__dot" />
                                                    <span className="support-chat__dot" />
                                                </div>
                                            </div>
                                        )}
                                        <div ref={endRef} />
                                    </>
                                )}
                            </div>

                            {showJumpToBottom && (
                                <button
                                    type="button"
                                    className="support-chat__jump-bottom"
                                    onClick={jumpToBottom}
                                    aria-label={t('supportChat.jumpToLatest', { defaultValue: 'Jump to latest messages' })}
                                >
                                    <ChevronDown size={18} aria-hidden="true" />
                                    <span>{t('supportChat.latest', { defaultValue: 'Latest' })}</span>
                                </button>
                            )}

                            <form className="support-chat__composer" onSubmit={handleSend}>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    className="support-chat__file-input"
                                    accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,.doc,.docx"
                                    onChange={handleFileSelect}
                                    tabIndex={-1}
                                />
                                <button
                                    type="button"
                                    className="support-chat__attach"
                                    onClick={() => fileInputRef.current?.click()}
                                    aria-label={t('supportChat.attach', { defaultValue: 'Attach file' })}
                                    disabled={sending}
                                >
                                    <Paperclip size={18} aria-hidden="true" />
                                </button>
                                <input
                                    ref={inputRef}
                                    type="text"
                                    className="support-chat__input"
                                    placeholder={t('supportChat.placeholder', { defaultValue: 'Type your message…' })}
                                    value={text}
                                    onChange={handleInputChange}
                                    autoComplete="off"
                                    enterKeyHint="send"
                                    maxLength={5000}
                                />
                                <button
                                    type="submit"
                                    className="support-chat__send"
                                    disabled={(!text.trim() && !pendingFile) || sending}
                                    aria-label={t('supportChat.send', { defaultValue: 'Send message' })}
                                >
                                    {sending ? (
                                        <Loader2 className="support-chat__spin" size={18} aria-hidden="true" />
                                    ) : (
                                        <Send size={18} aria-hidden="true" />
                                    )}
                                </button>
                            </form>
                            {sendError && (
                                <p className="support-chat__error" role="alert">{sendError}</p>
                            )}
                            {pendingFile && (
                                <div className="support-chat__pending-file">
                                    <span className="support-chat__pending-file-name">{pendingFile.name}</span>
                                    <button
                                        type="button"
                                        className="support-chat__pending-file-remove"
                                        onClick={() => setPendingFile(null)}
                                        aria-label={t('supportChat.removeAttachment', { defaultValue: 'Remove attachment' })}
                                    >
                                        <X size={14} aria-hidden="true" />
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {!open && (
                <button
                    type="button"
                    className="support-chat__launcher"
                    onClick={handleOpen}
                    aria-label={t('supportChat.open', { defaultValue: 'Open support chat' })}
                    aria-expanded={open}
                >
                    <MessageCircle size={24} aria-hidden="true" />
                    {unread > 0 && (
                        <span className="support-chat__badge" aria-label={`${unread} unread messages`}>
                            {unread > 9 ? '9+' : unread}
                        </span>
                    )}
                </button>
            )}
        </div>
    );
};

export default SupportChat;
